import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { extractResumeDataDocumentSection } from "../../../hooks/useResumeDocumentSection";
import { extractResumeProperties } from "../../../hooks/useResumeProperties";
import { calculateDatesDifference } from "../../../utils/calculateDatesDifference";
import { formatDate } from "../../../utils/formatDate";
import { parseISO } from 'date-fns';

export default function EuropassPdfExperiencesSection({ resumeData, translations }) {
    const { experiences } = extractResumeDataDocumentSection(resumeData);
    const { isAnonymous, documentLanguage } = extractResumeProperties(resumeData);

    const styles = StyleSheet.create({
        section: {
            flexDirection: 'row',
            fontSize: 8,
        },
        bold: {
            fontWeight: 'bold',
        },
        textDate: {
            fontSize: 9,
            marginBottom: 0,
        },
        category: {
            fontSize: 8,
            color: "#3F3F3F",
            marginBottom: 1,
            marginTop: 2,
            textAlign: 'start',
        },
        client: {
            fontSize: 8,
            color: "#3F3F3F",
            marginBottom: 1,
            marginTop: 1,
            textAlign: 'start',
        },
        lineTitle: {
            height: 1,
            backgroundColor: "#979797",
            marginBottom: 5,
        },
    });

    return (
        <View style={{ marginLeft: 20, marginRight: 40 }}>
            <View style={{ flexDirection: "row", marginBottom: 4 }}>
                <Text style={{ fontSize: 11, marginRight: 3 }}>•</Text>
                <Text style={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: 9,
                    marginTop: 2
                }}>
                    {translations.pdf.experiencesTitle}
                </Text>
            </View>
            <View style={[styles.lineTitle, { marginTop: 1 }]} />
            <View>
                {experiences
                    ?.sort((a, b) => parseISO(b?.from) - parseISO(a?.from))
                    .filter((experience) => experience)
                    .map((experience, index) => {

                        const validActionsMission = experience.actionsMission?.filter(
                            (action) => action?.text && action.text.trim()
                        ) || []; // FIXME
                        const validRoles = Array.isArray(experience.roles)
                            ? experience.roles.filter(role => role?.text && role.text.trim() !== '')
                            : [];
                        const employerText = isAnonymous
                            ? translations.pdf.employerLabel
                            : (experience?.employer?.trim?.() ? experience.employer : "");
                        const missionDays = experience?.duration
                            ?? experience?.numberOfMissionDays
                            ?? experience?.jourMission
                            ?? experience?.jourGlobal;
                        const hasMissionDays = missionDays !== null && missionDays !== undefined && missionDays !== '';

                        return (
                            <View key={`experience-${index}`} style={{ width: "95%" }}>
                                <View>
                                    <View style={[styles.fullColumn, { marginBottom: 4 }]}>
                                        <View style={{ flexDirection: "column" }}>
                                            <Text style={[
                                                styles.textDate,
                                                {
                                                    marginTop: 5,
                                                    textTransform: "uppercase",
                                                    fontSize: 8
                                                }
                                            ]}
                                            >
                                                {experience.from && (experience.to || experience.isCurrent)
                                                    ? formatDate(experience.from, documentLanguage) + ' - ' + (experience.isCurrent ? translations.pdf.isCurrentLabel : formatDate(experience.to, documentLanguage))
                                                    : formatDate(experience.from, documentLanguage)}
                                                {hasMissionDays && (
                                                    <>
                                                        {' '}( {missionDays} j/h )
                                                    </>
                                                )}
                                            </Text>
                                            <View style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                width: "95%",
                                                flexWrap: "wrap"
                                            }}>
                                                <Text style={[
                                                    styles.client,
                                                    styles.bold,
                                                    {
                                                        textTransform: "uppercase",
                                                        fontSize: 8
                                                    }
                                                ]}>
                                                    {experience.jobPosition}
                                                    {' '}
                                                </Text>
                                                {experience.isConsultingMission && (
                                                    <Text style={[
                                                        styles.missionText,
                                                        styles.bold,
                                                        {
                                                            color: "#3F3F3F",
                                                            textTransform: "uppercase",
                                                            fontSize: 8
                                                        }
                                                    ]}>
                                                        {translations.pdf.isConsultingMissionLabel}
                                                        {' '}
                                                        {isAnonymous && experience.client ? 'Client' : experience.client}
                                                    </Text>
                                                )}
                                                {employerText ? (
                                                    <Text style={{
                                                        color: "#3F3F3F",
                                                        textTransform: "uppercase",
                                                        fontSize: 8
                                                    }}>
                                                        - {employerText}
                                                    </Text>
                                                ) : null}
                                            </View>
                                            <View style={[styles.lineTitle]} />
                                        </View>
                                        {validRoles.length > 0 && (
                                            <View wrap={false}>
                                                <Text style={[styles.category, {
                                                    fontWeight: "bold",
                                                    textDecoration: "underline",
                                                    fontSize: 8
                                                }]}>
                                                    {translations.pdf.rolesLabel}
                                                </Text>
                                                <View>
                                                    {validRoles
                                                        .sort((a, b) => a.order - b.order)
                                                        .map((role, roleIndex) => (
                                                            <View key={roleIndex}
                                                                style={{ flexDirection: "column", width: "95%" }}>
                                                                <View
                                                                    style={{
                                                                        flexDirection: "row",
                                                                        paddingRight: 20
                                                                    }}>
                                                                    <Text style={{ marginHorizontal: 8 }}>•</Text>
                                                                    <Text style={{ fontSize: 8 }}>{' '}{role.text}</Text>
                                                                </View>
                                                            </View>
                                                        ))}
                                                </View>
                                            </View>
                                        )}
                                        {experience.context && experience.context.trim() !== "" && (
                                            <View wrap={false}>
                                                <Text style={[styles.category, {
                                                    fontWeight: "bold",
                                                    textDecoration: "underline",
                                                    fontSize: 8
                                                }]}>
                                                    {translations.pdf.contextLabel}
                                                </Text>
                                                <Text style={{ fontSize: 8 }}>
                                                    {experience.context}
                                                </Text>
                                            </View>
                                        )}
                                        {experience.achievement && experience.achievement.trim() !== "" && (
                                            <View wrap={false}>
                                                <Text style={[styles.category, {
                                                    fontWeight: "bold",
                                                    textDecoration: "underline",
                                                    fontSize: 8
                                                }]}>
                                                    {translations.pdf.achievementsLabel}
                                                </Text>
                                                <Text style={{ fontSize: 8 }}>
                                                    {experience.achievement}
                                                </Text>
                                            </View>
                                        )}
                                        {Array.isArray(experience.actions)
                                            && experience.actions.some(action => action?.text && action.text.trim() !== '')
                                            && (
                                                <View wrap={false}>
                                                    <Text style={[styles.category, {
                                                        fontWeight: "bold",
                                                        textDecoration: "underline",
                                                        fontSize: 8
                                                    }]}>
                                                        {translations.pdf.actionsLabel}
                                                    </Text>
                                                    <View>
                                                        {experience.actions
                                                            .filter(action => action?.text && action.text.trim() !== '')
                                                            .sort((a, b) => a.order - b.order)
                                                            .map((action, actionIndex) => (
                                                                <View key={actionIndex}
                                                                    style={{ flexDirection: "column", width: "95%" }}>
                                                                    <View
                                                                        style={{
                                                                            flexDirection: "row",
                                                                            paddingRight: 20
                                                                        }}>
                                                                        <Text style={{ marginHorizontal: 8 }}>•</Text>
                                                                        <Text
                                                                            style={{ fontSize: 8 }}>{' '}{action.text}</Text>
                                                                    </View>
                                                                </View>
                                                            ))}
                                                    </View>
                                                </View>
                                            )}
                                        <View wrap={false}>
                                            {validActionsMission.map((action, actionIndex) => (
                                                <View key={actionIndex}
                                                    style={{ flexDirection: "column", marginBottom: 4 }}>
                                                    <View style={{
                                                        flexDirection: "row",
                                                        marginBottom: 4,
                                                        paddingRight: 20
                                                    }}>
                                                        <Text style={{ marginHorizontal: 8 }}>•</Text>
                                                        <Text> {action.text}</Text>
                                                    </View>
                                                </View>
                                            ))}
                                        </View>

                                        {experience.technicalEnvironment && Array.isArray(experience.technicalEnvironment) && experience.technicalEnvironment.length > 0 && (
                                            <View wrap={false}>
                                                <Text style={[
                                                    styles.category,
                                                    {
                                                        fontWeight: "bold",
                                                        textDecoration: "underline",
                                                        fontSize: 8
                                                    }
                                                ]}>
                                                    {translations.pdf.technicalEnvironmentLabel}
                                                </Text>
                                                <View style={styles.section}>
                                                    <Text style={{ fontSize: 8 }}>
                                                        {experience.technicalEnvironment.map(skill => skill?.translations?.[documentLanguage] || skill).join(", ")}
                                                    </Text>
                                                </View>
                                            </View>
                                        )}
                                    </View>
                                </View>
                            </View>
                        );
                    })}
            </View>
        </View>
    );
}
